import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  isValidElement,
} from 'react';
import {
  ATMButton,
  ATMField,
  ATMForm,
  ATMModal,
  ATMInput,
  ATMTextArea,
} from 'shared-it-appmod-ui';
import { RequestStatus } from 'src/constants';
import Lang from 'src/libraries/language';
import { useRequestContext } from 'src/contexts/request.context';
import { RequestReasonSchema, IRequestReason } from 'src/models/request.model';
import { actionTypes } from 'src/ducks/request.duck';
import { getRequestStatus } from 'src/selectors/request.selector';

type ITrigger = (
  callback: React.Dispatch<React.SetStateAction<boolean | undefined>>
) => React.ReactNode;

type IProps = {
  requestId?: number;
  type?: string;
  open?: boolean;
  loading?: boolean;
  trigger?: ITrigger | React.ReactNode;
  onClose?: () => void;
  onSubmit?: (data: IRequestReason) => void;
  onSuccess?: (data) => void;
};

const FlightRequestChangeStatus: React.FC<IProps> = ({
  requestId,
  type = RequestStatus.Cancelled,
  open,
  loading,
  trigger,
  onClose,
  onSubmit,
  onSuccess,
}) => {
  const { state, actions } = useRequestContext();
  const formRef = useRef<HTMLFormElement>(null);
  const [isOpen, setIsOpen] = useState(open);

  const updateStatus = getRequestStatus(
    state,
    actionTypes.REQUEST_UPDATE_STATUS
  );

  const handleClick = useCallback(() => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  useEffect(() => {
    setIsOpen(open);
  }, [open, setIsOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);

    if (onClose) {
      onClose();
    }
  }, [onClose, setIsOpen]);

  const handleSubmit = useCallback(
    async (data) => {
      if (onSubmit) {
        onSubmit(data);
      } else if (requestId) {
        const result = await actions.updateStatusPUT(
          requestId,
          data.requestStatus,
          data.reason
        );

        if (result.payload) {
          setIsOpen(false);

          if (onSuccess) {
            onSuccess(Lang.MSG_REQUEST_CANCELLED);
          }
        }
      }
      handleClose();
    },
    [actions, requestId, onSuccess, onSubmit, setIsOpen]
  );

  let element;

  if (trigger) {
    element = isValidElement(trigger)
      ? trigger
      : (trigger as ITrigger)(setIsOpen);
  }

  return (
    <ATMModal
      size="small"
      open={isOpen}
      onClose={handleClose}
      trigger={element}
    >
      <ATMModal.Header>{Lang.TTL_CONFIRM_STATUS_CHANGE}</ATMModal.Header>
      <ATMModal.Content>
        <ATMForm
          key={requestId}
          ref={formRef}
          mode="onChange"
          onSubmit={handleSubmit}
          validationSchema={RequestReasonSchema}
        >
          {({ control, errors }) => (
            <>
              <ATMField
                name="requestStatus"
                as={ATMInput}
                control={control}
                type="hidden"
                className="hidden"
                defaultValue={type}
              />
              <ATMField
                label={
                  type === RequestStatus.Cancelled
                    ? Lang.LBL_REASON_CANCEL
                    : Lang.LBL_REASON_DECLINE
                }
                name="reason"
                as={ATMTextArea}
                control={control}
                error={errors.reason}
                required
                onClick={(e) => e.stopPropagation()}
                onFocus={((e) => e.stopPropagation()) as () => void}
              />
            </>
          )}
        </ATMForm>
      </ATMModal.Content>
      <ATMModal.Actions>
        <ATMButton secondary type="button" onClick={handleClose}>
          {Lang.LBL_CANCEL}
        </ATMButton>
        <ATMButton
          primary
          type="button"
          loading={updateStatus.fetching || loading}
          disabled={updateStatus.fetching || loading}
          onClick={handleClick}
        >
          {Lang.LBL_SUBMIT}
        </ATMButton>
      </ATMModal.Actions>
    </ATMModal>
  );
};

export default FlightRequestChangeStatus;
